"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { tick, setup, arrow, website } from "@/assets/img/health";
import { useRouter } from "next/navigation";

interface HealthCheckModalProps {
  forceOpen?: boolean;
  onCloseCallback?: () => void;
}

/**
 * HealthCheckModal Component
 * Replicates the exact design of /website-security-check in a pop-up modal.
 * Auto-opens on website load unless dismissed during the current session.
 *
 * @param {HealthCheckModalProps} props Component props
 * @returns {React.JSX.Element} Health Check Modal Component
 */
const HealthCheckModal: React.FC<HealthCheckModalProps> = ({
  forceOpen = false,
  onCloseCallback,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      return;
    }

    const hasClosedModal = sessionStorage.getItem("has_closed_security_modal");
    if (!hasClosedModal) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [forceOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    sessionStorage.setItem("has_closed_security_modal", "true");
    onCloseCallback?.();
  }, [onCloseCallback]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (overlayRef.current && e.target === overlayRef.current) {
      handleClose();
    }
  };

  const handleCardClick = (path: string) => {
    handleClose();
    router.push(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
        >
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[1042px] my-auto overflow-hidden rounded-[24px] flex flex-col items-center p-6 sm:p-10 md:p-12"
            style={{
              fontFamily: "'Inter', sans-serif",
              background: "#F7FAF2",
              backgroundImage:
                "linear-gradient(180deg, #F7FAF2 0%, #FFFFFF 100%)",
              maxHeight: "92vh",
              overflowY: "auto",
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none z-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)",
              }}
            />

            <button
              onClick={handleClose}
              type="button"
              aria-label="Close modal"
              className="absolute top-4 bg-white right-4 z-20 w-10 h-10 rounded-full  items-center justify-center flex border border-[#E3EDD1] text-[#6F9E24] hover:bg-[#F1FAE9] hover:border-[#8ABA28] hover:scale-105 transition-all cursor-pointer focus:outline-none"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="w-full flex flex-col items-center relative z-10">
              <div className="inline-flex justify-center items-center gap-2.5">
                <Image
                  src={tick}
                  alt="Friendly Health Check"
                  width={32}
                  height={32}
                />
                <div className="bg-[#EDF7DD] border border-[#D8EAB8] px-4 py-2 rounded-full">
                  <span className="text-[#5D8D1F] text-xs sm:text-sm font-medium tracking-tight">
                    A Friendly Health Check For Your Business
                  </span>
                </div>
              </div>

              <h2
                className="text-2xl sm:text-4xl md:text-[44px] font-semibold text-[#1D1D1D] tracking-normal leading-tight text-center w-full mt-4 sm:leading-[54px] max-w-2xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                See how well your business is protected online
              </h2>

              <p className="mt-3 text-[#5B5B5B] text-sm sm:text-base text-center max-w-2xl mx-auto leading-relaxed font-normal">
                A quick, no-jargon look at where you stand. No technical
                knowledge needed &mdash; do one check or both, it only takes a
                couple of minutes.
              </p>

              {/* Cards Grid */}
              <div className="flex flex-col md:flex-row items-stretch justify-center gap-5 sm:gap-6 w-full max-w-[960px] mt-8 mb-6">
                {/* Card 1: Business Security Check */}
                <div
                  onClick={() => handleCardClick("/assessment-form")}
                  className="w-full md:w-1/2 min-h-[260px] sm:min-h-[280px] p-6 sm:p-8 bg-white rounded-[18px] border border-[#E3EDD1] shadow-sm hover:shadow-lg hover:border-[#8ABA28] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between relative cursor-pointer group overflow-hidden"
                >
                  <div className="absolute right-8 top-4 w-28 h-28 rounded-full bg-[#A9D84B] blur-[80px] opacity-60 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute left-6 bottom-6 w-24 h-24 rounded-full bg-[#D8F2A8] blur-[70px] opacity-70 group-hover:opacity-100 transition-opacity" />

                  <div className="relative z-10 flex flex-col gap-5">
                    <Image
                      src={setup}
                      alt="Check your setup"
                      width={52}
                      height={52}
                    />
                    <div>
                      <h3
                        className="text-xl sm:text-2xl font-semibold text-[#1D1D1D] mb-2 tracking-tight group-hover:text-[#6F9E24] transition-colors"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Business Security Check
                      </h3>
                      <p className="text-[#5B5B5B] text-xs sm:text-sm font-normal leading-relaxed">
                        A 2-minute questionnaire about how you work day to day.
                        No technical knowledge needed.
                      </p>
                    </div>
                  </div>

                  <div className="relative z-10 mt-6 pt-2">
                    <span className="inline-flex items-center gap-2 text-[#6F9E24] font-medium text-sm sm:text-base group-hover:text-[#5D8D1F] group-hover:translate-x-1 transition-all">
                      <span>Start The Questionnaire</span>
                      <Image
                        src={arrow}
                        alt="Arrow icon"
                        width={20}
                        height={20}
                      />
                    </span>
                  </div>
                </div>

                {/* Card 2: Website Exposure Check */}
                <div
                  onClick={() => handleCardClick("/scan-website")}
                  className="w-full md:w-1/2 min-h-[260px] sm:min-h-[280px] p-6 sm:p-8 bg-white rounded-[18px] border border-[#E3EDD1] shadow-sm hover:shadow-lg hover:border-[#8ABA28] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between relative cursor-pointer group overflow-hidden"
                >
                  <div className="absolute right-8 top-4 w-28 h-28 rounded-full bg-[#A9D84B] blur-[80px] opacity-60 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute left-6 bottom-6 w-24 h-24 rounded-full bg-[#D8F2A8] blur-[70px] opacity-70 group-hover:opacity-100 transition-opacity" />

                  <div className="relative z-10 flex flex-col gap-5">
                    <Image
                      src={website}
                      alt="Website Exposure Check"
                      width={52}
                      height={52}
                    />
                    <div>
                      <h3
                        className="text-xl sm:text-2xl font-semibold text-[#1D1D1D] mb-2 tracking-tight group-hover:text-[#6F9E24] transition-colors"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Website Exposure Check
                      </h3>
                      <p className="text-[#5B5B5B] text-xs sm:text-sm font-normal leading-relaxed">
                        Enter your domain and see what an outsider can observe
                        from the public internet.
                      </p>
                    </div>
                  </div>

                  <div className="relative z-10 mt-6 pt-2">
                    <span className="inline-flex items-center gap-2 text-[#6F9E24] font-medium text-sm sm:text-base group-hover:text-[#5D8D1F] group-hover:translate-x-1 transition-all">
                      <span>Run Website Exposure Check</span>
                      <Image
                        src={arrow}
                        alt="Arrow icon"
                        width={20}
                        height={20}
                      />
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer Note */}
              <p className="text-[#5B5B5B] text-xs sm:text-sm font-normal text-center">
                You can do either or both &mdash; whatever&apos;s useful.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default HealthCheckModal;
